import { MobileSlidingContainer } from '@cian/frontend-mobile-sliding-container';
import { Responsive } from '@cian/ui-kit';
import classNames from 'classnames';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as style from './index.css';
import { setEditableMessage } from '../../actions/chat/actions';
import { ChatsErrorPage } from '../../components/ChatsErrorPage';
import { MobileLayout } from '../../components/MobileLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useMobileBottomNavigation } from '../../hooks/useMobileBottomNavigationEnabled';
import { currentOpenChatIDSelector, currentOpenChatSelector, isMasterSelector, userSelector } from '../../selectors';
import { selectShowErrorPage } from '../../selectors/errorPageSelector';
import { startPollingMessages, stopPollingMessages } from '../../thunks/chatbot';
import { getChatbotActionButtons } from '../../thunks/chatbot/getChatbotActionButtons';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { TAppState } from '../../types/appState';
import { EHostType } from '../../types/hostType';
import { useApplicationContext } from '../../utils/applicationContext';
import { isSubAgentsChatsEnabled } from '../../utils/isSubAgentsChatsEnabled';
import { useAnchorTargetBlank } from '../../utils/useAnchorTargetBlank';
import { AlertContainer } from '../AlertContainer';
import { AuthNotificationContainer } from '../AuthNotificationContainer';
import { BottomNavigationContainer } from '../BottomNavigationContainer';
import { ChatActionModalContainer } from '../ChatActionModalContainer';
import { ChatListContainer } from '../ChatListContainer';
import { ChatWindowContainer } from '../ChatWindowContainer';
import { ChatbotFiltersModalContainer } from '../ChatbotFiltersModalContainer';
import { CustomActionModalContainer } from '../CustomActionModalContainer';
import { DailyrentBookingCalendarSettingsModalContainer } from '../DailyrentBookingCalendarSettingsModalContainer';
import { DailyrentReviewModalContainer } from '../DailyrentReviewModalContainer';
import { DeleteMessageModalContainer } from '../DeleteMessageModalContainer';
import { FeedbackChatbotModalContainer } from '../FeedbackChatbotModalContainer';
import { MasterTabsContainer } from '../MasterTabsContainer';
import { ReplyTemplateModalContainer } from '../ReplyTemplateModalContainer';
import { UserCardContainer } from '../UserCardContainer';
import { useFocusChange, useBroadcastChannelEventListener } from './hooks';

export const PageContainer: React.FC = () => {
  const {
    config,
    custom: { hostType },
  } = useApplicationContext();

  const dispatch = useDispatch();

  useAnchorTargetBlank();
  const activeSide = useSelector((state: TAppState) => state.mobileSide);
  const showErrorPage = useSelector(selectShowErrorPage);
  const { isAuthenticated } = useSelector(userSelector);
  const currentOpenChatID = useSelector(currentOpenChatIDSelector);
  const isChatbotActive = currentOpenChatID === CHATBOT_ID;
  const isChatOpen = currentOpenChatID !== undefined;
  const currentChat = useSelector(currentOpenChatSelector);
  const isMaster = useSelector(isMasterSelector);
  const isMobileBottomNavigationEnabled = useMobileBottomNavigation();
  const isMasterTabsEnabled = isMaster && isSubAgentsChatsEnabled(config);
  const isDesktopFrame = hostType === EHostType.DesktopFrame;

  const { editableMessage, defaultMessagePayload } = currentChat || {};

  const handeRetry = React.useCallback(() => {
    window.location.reload();
  }, []);

  useFocusChange();

  useBroadcastChannelEventListener();

  React.useEffect(() => {
    if (hostType !== EHostType.Frame || isChatbotActive) {
      dispatch(startPollingMessages({ isQuick: true }));
    }

    return () => {
      dispatch(stopPollingMessages());
    };
  }, [dispatch, hostType, isChatbotActive]);

  React.useEffect(() => {
    const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled') || false;

    if (!isChatbotGPTEnabled) {
      return;
    }

    if (isChatbotActive) {
      dispatch(getChatbotActionButtons('initial'));

      if (editableMessage && defaultMessagePayload) {
        const offerId = parseInt(defaultMessagePayload, 10);
        dispatch(sendChatbotMessage(editableMessage, offerId));
        dispatch(setEditableMessage({ chatId: CHATBOT_ID, text: '' }));
      }

      return;
    }

    if (isChatOpen) {
      dispatch(getChatbotActionButtons('closing'));
    }
  }, [dispatch, config, isChatbotActive, isChatOpen]);

  if (showErrorPage) {
    return (
      <div className={style['chats_container']}>
        <ChatsErrorPage onRetry={handeRetry} />
      </div>
    );
  }

  const containerClassName = classNames(
    style['chats_container'],
    isMobileBottomNavigationEnabled && style['with_bottom_navigation'],
    isDesktopFrame && style['chats_container--desktop_frame'],
  );

  return (
    <>
      <Responsive phone tablet>
        <div className={containerClassName}>
          <MobileLayout
            activeSide={activeSide}
            leftView={<ChatListContainer />}
            centerView={<ChatWindowContainer />}
            rightView={<UserCardContainer />}
          />
          <AlertContainer />
          <ChatActionModalContainer />
          <CustomActionModalContainer />
          <FeedbackChatbotModalContainer />
          <MobileSlidingContainer>
            <BottomNavigationContainer isEnabled={isMobileBottomNavigationEnabled} />
          </MobileSlidingContainer>
        </div>
      </Responsive>
      <Responsive desktop>
        <div className={containerClassName}>
          <div className={style['list']}>{isMasterTabsEnabled ? <MasterTabsContainer /> : <ChatListContainer />}</div>
          <div className={style['chat']}>
            {isAuthenticated || isChatbotActive ? <ChatWindowContainer /> : <AuthNotificationContainer />}
            <AlertContainer />
          </div>
          <UserCardContainer />
          <ChatActionModalContainer />
          <CustomActionModalContainer />
          <FeedbackChatbotModalContainer />
        </div>
      </Responsive>
      <ChatbotFiltersModalContainer />
      <DailyrentReviewModalContainer />
      <DailyrentBookingCalendarSettingsModalContainer />
      <DeleteMessageModalContainer />
      <ReplyTemplateModalContainer />
    </>
  );
};
